import type { ClipboardEvent } from 'react';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  customDateFormatter,
  intl,
  intlFmt,
  NotificationType,
  toast
} from '../../lib';
import { OtpInput } from './OtpInput';
import { InlineError, Maskable } from '../basic/';
import { isNumericString, removeWhiteSpaces } from '../../util';
import { TextInput } from '../form';

enum SendingState {
  NotSent,
  Sending,
  Sent
}

const RESEND_TIMEOUT_SECONDS = 90;

type OtpConfirmationControlSize = 'default' | 'small';

const initializeInputValues = (lengthOfCode: number) => {
  return Array.from({ length: lengthOfCode }, () => '#');
};

type ErrorObj = {
  isError: boolean;
  errorMsg: string | undefined;
};

function createNoErrorObj(): ErrorObj {
  return {
    isError: false,
    errorMsg: undefined
  };
}

function ResendTimer({
  initialTimeLeft = RESEND_TIMEOUT_SECONDS,
  onTimeout,
  size
}: Readonly<{
  initialTimeLeft?: number;
  onTimeout: () => void;
  size: OtpConfirmationControlSize;
}>): React.JSX.Element {
  const [timeLeft, setTimeLeft] = useState(initialTimeLeft);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    intervalIdRef.current = setInterval(() => {
      setTimeLeft(prevTimeLeft => {
        if (prevTimeLeft === 0) {
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
          }
          onTimeoutRef.current();
          return 0;
        }
        return prevTimeLeft - 1;
      });
    }, 1000);

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, []);

  const printableTime = customDateFormatter({
    second: '2-digit',
    minute: 'numeric'
  }).formatDate(new Date(timeLeft * 1000));

  return (
    <p
      className={`otp-resend-timer ${size === 'small' ? 'font-14' : 'font-16'}`}
    >
      {intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeResendTimer',
        printableTime
      )}
    </p>
  );
}

function OtpConfirmationBoundary({
  masked,
  children
}: Readonly<{
  masked: boolean;
  children: React.JSX.Element;
}>): React.JSX.Element {
  return (
    <div className="ehs-otp-confirmation-control">
      <Maskable
        masked={masked}
        className="authentication-content-loading-spinner"
      >
        {children}
      </Maskable>
    </div>
  );
}

export type OtpChannel = 'SMS' | 'EMAIL' | 'TOTP';
export type OtpDetails = {
  sendMethod: OtpChannel;
  recipient: string;
};

export type OtpAuthType = 'OTP' | 'RECOVERY';

export type OtpConfirmationControlHandle = {
  reset: () => void;
  submitOtp: () => void;
  submitRecoveryCode: () => void;
};

export const OtpConfirmationControl = function OtpConfirmationControl({
  autoFocusFirstInput,
  onOtpValid,
  onOtpEntered,
  lengthOfCode,
  validateAuthenticationCodeCall,
  sendAuthenticationCodeCall,
  initialOtpDetails,
  initialResendTimeLeft,
  disabled = false,
  autoSubmit = false,
  ref,
  authType = 'OTP',
  size = 'default'
}: Readonly<{
  autoFocusFirstInput?: boolean;
  onOtpValid: () => void;
  onOtpEntered?: (code: string) => void;
  lengthOfCode: number;
  validateAuthenticationCodeCall: (
    authenticationCode: string,
    onCodeMatched: () => void,
    onCodeMismatched: (msg?: string) => void,
    onRequestStatusChanged: (pending: boolean) => void,
    authType?: OtpAuthType
  ) => void;
  sendAuthenticationCodeCall: (
    onSendingSuccess: (sendDetails: OtpDetails) => void,
    onSendingError: (msg?: string, resendDisabled?: boolean) => void
  ) => void;
  initialOtpDetails?: OtpDetails;
  initialResendTimeLeft?: number;
  disabled?: boolean;
  autoSubmit?: boolean;
  authType?: OtpAuthType;
  size?: OtpConfirmationControlSize;
}> & { ref?: React.Ref<unknown> }): React.JSX.Element {
  const initialState = initialOtpDetails
    ? SendingState.Sent
    : SendingState.NotSent;
  const [sendingState, setSendingState] = useState(initialState);
  const [sendingError, setSendingError] = useState<ErrorObj>(
    createNoErrorObj()
  );
  const [hasValidationError, setHasValidationError] = useState<ErrorObj>(
    createNoErrorObj()
  );
  const [authenticationCode, setAuthenticationCode] = useState(
    initializeInputValues(lengthOfCode)
  );
  const [isConfirmRequestPending, setIsConfirmRequestPending] = useState(false);
  const [isResendDisabledAfterError, setIsResendDisabledAfterError] = useState(
    false
  );
  const initialSendingState = useMemo(() => {
    return (
      initialOtpDetails || {
        sendMethod: 'TOTP' as OtpChannel,
        recipient: ''
      }
    );
  }, [initialOtpDetails]);
  const sanitizedInitialResendTimeLeft = Math.max(
    initialResendTimeLeft ?? 0,
    0
  );
  const [showResendTimer, setShowResendTimer] = useState(
    initialState === SendingState.Sent &&
      initialSendingState.sendMethod !== 'TOTP' &&
      sanitizedInitialResendTimeLeft > 0
  );
  const [sendingDetails, setSendingDetails] = useState(initialSendingState);
  const resendTimerEnabled = sendingDetails.sendMethod !== 'TOTP';
  const [isTanSent, setIsTanSent] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const textSizeClass = size === 'small' ? 'font-14' : 'font-16';
  useEffect(() => {
    if (sendingState === SendingState.Sent && isTanSent) {
      setShowResendTimer(resendTimerEnabled);
    }
  }, [sendingState, resendTimerEnabled, isTanSent]);

  const submitCode = (type: OtpAuthType) => {
    setHasValidationError(createNoErrorObj());
    const codeToSubmit =
      type === 'RECOVERY' ? recoveryCode : authenticationCode.join('');
    validateAuthenticationCode(codeToSubmit);
  };

  // Expose the reset method via ref
  useImperativeHandle(ref, () => ({
    reset: () => {
      setSendingState(initialState);
      setSendingError(createNoErrorObj());
      setHasValidationError(createNoErrorObj());
      setAuthenticationCode(initializeInputValues(lengthOfCode));
      setRecoveryCode('');
      setIsConfirmRequestPending(false);
      setIsResendDisabledAfterError(false);
      setShowResendTimer(false);
      setSendingDetails(initialSendingState);
      setIsTanSent(false);
      onOtpEntered?.('');
    },
    submitOtp: () => {
      submitCode('OTP');
    },
    submitRecoveryCode: () => {
      submitCode('RECOVERY');
    }
  }));

  const triggerResend = () => {
    setSendingState(SendingState.NotSent);
    setHasValidationError(createNoErrorObj());
    setSendingError(createNoErrorObj());
    setIsResendDisabledAfterError(false);
  };

  const getActivationCodeSentToastMessage = (
    sendingDetails: OtpDetails
  ): string | undefined => {
    if (sendingDetails.sendMethod === 'EMAIL') {
      return intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeSentViaEmailToast',
        sendingDetails.recipient
      );
    } else if (sendingDetails.sendMethod === 'SMS') {
      return intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeSentViaSmsToast',
        sendingDetails.recipient
      );
    }
    return undefined;
  };

  const getActivationCodeSentDescription = (
    sendingDetails: OtpDetails,
    lengthOfCode: number
  ): string => {
    if (authType === 'RECOVERY') {
      return intl('loginhook.twofactor.view.form.recovery.app');
    }
    if (sendingDetails.sendMethod === 'EMAIL') {
      return intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeEmailDescription',
        lengthOfCode,
        sendingDetails.recipient
      );
    } else if (sendingDetails.sendMethod === 'SMS') {
      return intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeSmsDescription',
        lengthOfCode,
        sendingDetails.recipient
      );
    } else if (sendingDetails.sendMethod === 'TOTP') {
      return intl('loginhook.twofactor.view.form.otp.app');
    }
    return '';
  };

  const getErrorMessageContent = (): string => {
    if (hasValidationError.errorMsg) {
      return hasValidationError.errorMsg;
    }

    return authType === 'RECOVERY'
      ? intl('error.CODED.PL00805.msg')
      : intl(
          'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.validateActivationCodeFailedText'
        );
  };

  const validateAuthenticationCode = useCallback(
    (authenticationCode: string) => {
      validateAuthenticationCodeCall(
        removeWhiteSpaces(authenticationCode),
        () => onOtpValid(),
        (errorMsg?) => {
          onOtpEntered?.('');
          if (authType === 'RECOVERY') {
            setRecoveryCode('');
          } else {
            setAuthenticationCode(initializeInputValues(lengthOfCode));
          }
          setHasValidationError({
            isError: true,
            errorMsg: errorMsg
          });
        },
        (pending: boolean) => setIsConfirmRequestPending(pending),
        authType
      );
    },
    [
      validateAuthenticationCodeCall,
      onOtpValid,
      onOtpEntered,
      lengthOfCode,
      authType
    ]
  );

  const onRecoveryCodeChange = useCallback(
    (currentCode: string) => {
      setHasValidationError(createNoErrorObj());
      setRecoveryCode(currentCode);
      if (currentCode.length >= lengthOfCode && isNumericString(currentCode)) {
        onOtpEntered?.(currentCode);
        if (!autoSubmit) {
          return false;
        }
        validateAuthenticationCode(currentCode);
        return true;
      } else {
        onOtpEntered?.('');
      }
      return false;
    },
    [autoSubmit, lengthOfCode, onOtpEntered, validateAuthenticationCode]
  );

  const onOptInputChange = useCallback(
    (authCode: string[]) => {
      setHasValidationError(createNoErrorObj());
      setAuthenticationCode(authCode);

      const currentCode = authCode.join('');
      if (currentCode.length >= lengthOfCode && isNumericString(currentCode)) {
        onOtpEntered?.(currentCode);
        if (!autoSubmit) {
          return false;
        }
        validateAuthenticationCode(currentCode);
        return true;
      } else {
        onOtpEntered?.('');
      }
      return false;
    },
    [autoSubmit, lengthOfCode, onOtpEntered, validateAuthenticationCode]
  );
  const onBadCodePasted = () => {
    toast(
      authType === 'RECOVERY'
        ? intl('loginhook.twofactor.view.form.recovery.invalid.title')
        : intl(
            'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.validateActivationCodeFailedTitle'
          ),
      NotificationType.WARN,
      undefined
    );
  };
  const onPasteFunction = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    if (disabled) {
      return;
    }

    const content = e.clipboardData.getData('text');
    const digits = content.split('').filter(isNumericString);

    if (digits.length >= lengthOfCode) {
      const pastedCode = digits.join('');
      setRecoveryCode(pastedCode);
      onOtpEntered?.(pastedCode);
    } else {
      onBadCodePasted();
    }
  };
  useEffect(() => {
    if (
      isTanSent &&
      sendingState === SendingState.Sent &&
      sendingDetails.recipient.length > 0
    ) {
      const toastMsg = getActivationCodeSentToastMessage(sendingDetails);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      toastMsg && toast(toastMsg, NotificationType.SUCCESS, undefined);
    }
  }, [isTanSent, sendingState, sendingDetails]);

  // Trigger sending authentication code when state is NotSent
  useEffect(() => {
    if (sendingState !== SendingState.NotSent) {
      return;
    }

    setSendingState(SendingState.Sending);
    sendAuthenticationCodeCall(
      (sendDetails: OtpDetails) => {
        setSendingDetails(sendDetails);
        setSendingState(SendingState.Sent);
        setIsTanSent(true);
      },
      (msg?: string, resendDisabled?: boolean) => {
        if (resendDisabled) {
          setIsResendDisabledAfterError(true);
        }
        setSendingError({
          isError: true,
          errorMsg: msg
        });
      }
    );
  }, [sendingState, sendAuthenticationCodeCall]);

  // Render based on current state
  if (sendingState === SendingState.Sent) {
    const showResendLink =
      authType !== 'RECOVERY' &&
      resendTimerEnabled &&
      !isResendDisabledAfterError &&
      !isConfirmRequestPending &&
      !showResendTimer;
    return (
      <OtpConfirmationBoundary masked={isConfirmRequestPending}>
        <div className="ehs-otp-confirmation-control-content">
          <p
            className={`activation-code-description ${textSizeClass}`}
            id="activation-code-sent-description"
          >
            {getActivationCodeSentDescription(sendingDetails, lengthOfCode)}
          </p>
          {hasValidationError.isError && (
            <InlineError
              title={
                authType === 'RECOVERY'
                  ? intl('loginhook.twofactor.view.form.recovery.invalid.title')
                  : intl(
                      'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.validateActivationCodeFailedTitle'
                    )
              }
              text={getErrorMessageContent()}
            />
          )}
          {authType !== 'RECOVERY' && (
            <OtpInput
              autoFocusFirstInput={autoFocusFirstInput}
              authenticationCode={authenticationCode}
              lengthOfCode={lengthOfCode}
              onChange={onOptInputChange}
              hasError={hasValidationError.isError}
              ariaLabelOfDigit="patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.enterDigitHint"
              ariaDescribedByIdOfDigit="activation-code-sent-description"
              onBadCodePasted={onBadCodePasted}
              disabled={disabled}
              onSubmit={() => submitCode('OTP')}
            />
          )}
          {authType === 'RECOVERY' && (
            <div
              className={`recovery-code-container otp-input-container${
                hasValidationError.isError ? ' error' : ''
              }`}
            >
              <TextInput
                label={intl('loginhook.twofactor.view.form.recovery.link')}
                type="text"
                inputMode="numeric"
                className={'recovery-code-input'}
                autoFocus={autoFocusFirstInput ?? true}
                disabled={disabled}
                value={recoveryCode}
                onChange={value => onRecoveryCodeChange(value)}
                aria-label={intl('loginhook.twofactor.view.form.recovery.link')}
                aria-describedby="activation-code-sent-description"
                autoComplete="one-time-code"
                onPaste={onPasteFunction}
                onFocus={() => {
                  if (!isNumericString(recoveryCode)) {
                    setRecoveryCode('');
                  }
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    submitCode('RECOVERY');
                  }
                }}
              ></TextInput>
            </div>
          )}
          {showResendLink && (
            <p className={`otp-resend-link-container ${textSizeClass}`}>
              {sendingDetails.sendMethod === 'EMAIL'
                ? intl(
                    'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeTakingTooLong'
                  )
                : intl(
                    'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeTakingTooLong.sms'
                  )}
              &nbsp;
              <button
                className="otp-resend-link"
                onClick={() => triggerResend()}
                onKeyUp={e => {
                  if (e.key === ' ') {
                    triggerResend();
                  }
                }}
              >
                {intl(
                  'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeSendAgain'
                )}
              </button>
            </p>
          )}
          {showResendTimer && (
            <ResendTimer
              initialTimeLeft={
                isTanSent
                  ? RESEND_TIMEOUT_SECONDS
                  : sanitizedInitialResendTimeLeft
              }
              onTimeout={() => {
                setShowResendTimer(false);
              }}
              size={size}
            />
          )}
        </div>
      </OtpConfirmationBoundary>
    );
  } else if (sendingError.isError) {
    return (
      <OtpConfirmationBoundary masked={isConfirmRequestPending}>
        <InlineError
          title={intl(
            'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.sendingActivationCodeFailedTitle'
          )}
          text={
            sendingError.errorMsg ??
            intl(
              'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.sendingActivationCodeFailedText'
            )
          }
        />
      </OtpConfirmationBoundary>
    );
  } else {
    return (
      <OtpConfirmationBoundary masked={true}>
        <p className={textSizeClass}>
          {intl(
            'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.sendingActivationCode'
          )}
        </p>
      </OtpConfirmationBoundary>
    );
  }
};
